import React, {useEffect, useState} from 'react';
import {
    Button,
    CircularProgress,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import {getUsersByRole} from '../../../firebase/firestore/users';
import {collectionAssigneeChatParentId} from '../../../firebase/firestore/chats';
import AuthorChip from '../../products/components/common/AuthorChip';

function userDisplayName(u) {
    if (!u) return null;
    const fromParts = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return fromParts || u.displayName || u.name || u.email || null;
}

function toAvatarProfile(u) {
    if (!u) return null;
    return {
        displayName: userDisplayName(u),
        hasAvatar: u.hasAvatar === true,
        profilePicture: u.profilePicture || u.photoURL || undefined,
    };
}

/**
 * Assignee metadata: shows the current assignee or "Unassigned". CM/admin can
 * pick a collector or clear assignment; collectors can claim unassigned tasks.
 */
export default function CollectionTaskAssigneeField({
    assigneeId,
    profile,
    canManage = false,
    canClaim = false,
    disabled = false,
    onAssign,
    onClaim,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [collectors, setCollectors] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!canManage) return undefined;
        let cancelled = false;
        setLoading(true);
        getUsersByRole('collector')
            .then((users) => { if (!cancelled) setCollectors(users || []); })
            .catch(() => { if (!cancelled) setCollectors([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canManage]);

    const options = collectors
        .map((u) => ({
            id: u.id || u.userId,
            displayName: userDisplayName(u) || u.id || u.userId,
        }))
        .filter((u) => u.id);

    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);
    const pick = (userId) => {
        closeMenu();
        onAssign?.(userId);
    };
    const chatParentId = collectionAssigneeChatParentId(assigneeId);

    return (
        <Stack spacing={0.25} minWidth={0} data-testid="ct-assignee-field">
            <Typography variant="caption" color="text.secondary">Assignee</Typography>
            {assigneeId ? (
                canManage ? (
                    <>
                        <Button
                            size="small"
                            variant="text"
                            disabled={disabled}
                            onClick={openMenu}
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                px: 0,
                                minWidth: 0,
                            }}
                        >
                            <AuthorChip
                                userId={assigneeId}
                                userDisplayName={userDisplayName(profile) || assigneeId}
                                flex={undefined}
                                avatarProfile={toAvatarProfile(profile)}
                                chatParentId={chatParentId}
                            />
                        </Button>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                            {loading && (
                                <MenuItem disabled><CircularProgress size={16}/></MenuItem>
                            )}
                            {!loading && (
                                <MenuItem onClick={() => pick(null)}>Unassigned</MenuItem>
                            )}
                            {!loading && options.map((u) => (
                                <MenuItem
                                    key={u.id}
                                    selected={u.id === assigneeId}
                                    onClick={() => pick(u.id)}
                                >
                                    {u.displayName}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                ) : (
                    <AuthorChip
                        userId={assigneeId}
                        userDisplayName={userDisplayName(profile) || assigneeId}
                        flex={undefined}
                        avatarProfile={toAvatarProfile(profile)}
                        chatParentId={chatParentId}
                    />
                )
            ) : (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                    {canManage && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={disabled}
                                onClick={openMenu}
                            >
                                Assign
                            </Button>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                                {loading && (
                                    <MenuItem disabled><CircularProgress size={16}/></MenuItem>
                                )}
                                {!loading && options.length === 0 && (
                                    <MenuItem disabled>No collectors available</MenuItem>
                                )}
                                {!loading && options.map((u) => (
                                    <MenuItem key={u.id} onClick={() => pick(u.id)}>
                                        {u.displayName}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                    {!canManage && canClaim && (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={disabled}
                            onClick={onClaim}
                        >
                            Assign to me
                        </Button>
                    )}
                </Stack>
            )}
        </Stack>
    );
}
